<template>
    <BaseForm v-if="series">
        <template #fields>
            <TextAreaField
                :value="values.notesHallExhibitionAreas"
                :label="$t('notes-hall-exhibition-areas')"
                :error-messages="errors.notesHallExhibitionAreas"
                @input="setFieldValue('notesHallExhibitionAreas', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('notesHallExhibitionAreas', true)"
            />
            <TextAreaField
                :value="values.notesBoulevardSections"
                :label="$t('notes-boulevard-sections')"
                :error-messages="errors.notesBoulevardSections"
                @input="setFieldValue('notesBoulevardSections', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('notesBoulevardSections', true)"
            />
            <TextAreaField
                :value="values.notesPassages"
                :label="$t('notes-passages')"
                :error-messages="errors.notesPassages"
                @input="setFieldValue('notesPassages', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('notesPassages', true)"
            />
            <TextAreaField
                :value="values.notesEntrances"
                :label="$t('notes-entrances')"
                :error-messages="errors.notesEntrances"
                @input="setFieldValue('notesEntrances', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('notesEntrances', true)"
            />
            <TextAreaField
                :value="values.notesServiceCenters"
                :label="$t('notes-service-centers')"
                :error-messages="errors.notesServiceCenters"
                @input="setFieldValue('notesServiceCenters', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('notesServiceCenters', true)"
            />
            <TextAreaField
                :value="values.notesConferenceRooms"
                :label="$t('notes-conference-rooms')"
                :error-messages="errors.notesConferenceRooms"
                @input="setFieldValue('notesConferenceRooms', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('notesConferenceRooms', true)"
            />
            <TextAreaField
                :value="values.notesOfficesInternal"
                :label="$t('notes-offices-internal')"
                :error-messages="errors.notesOfficesInternal"
                @input="setFieldValue('notesOfficesInternal', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('notesOfficesInternal', true)"
            />
            <TextAreaField
                :value="values.notesStorageInternal"
                :label="$t('notes-storage-internal')"
                :error-messages="errors.notesStorageInternal"
                @input="setFieldValue('notesStorageInternal', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('notesStorageInternal', true)"
            />
            <TextAreaField
                :value="values.notesOfficesExternal"
                :label="$t('notes-offices-external')"
                :error-messages="errors.notesOfficesExternal"
                @input="setFieldValue('notesOfficesExternal', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('notesOfficesExternal', true)"
            />
            <TextAreaField
                :value="values.notesStorageExternal"
                :label="$t('notes-storage-external')"
                :error-messages="errors.notesStorageExternal"
                @input="setFieldValue('notesStorageExternal', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('notesStorageExternal', true)"
            />
            <TextAreaField
                :value="values.notesAreas"
                :label="$t('notes-areas')"
                :error-messages="errors.notesAreas"
                @input="setFieldValue('notesAreas', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('notesAreas', true)"
            />
            <TextAreaField
                :value="values.notesParking"
                :label="$t('notes-parking')"
                :error-messages="errors.notesParking"
                @input="setFieldValue('notesParking', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('notesParking', true)"
            />
            <TextAreaField
                :value="values.otherAllocationNeeds"
                :label="$t('other-allocation-notes')"
                :error-messages="errors.otherAllocationNeeds"
                @input="setFieldValue('otherAllocationNeeds', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('otherAllocationNeeds', true)"
            />
        </template>
    </BaseForm>
</template>
<script setup lang="ts">
import BaseForm from '@/components/Form/BaseForm.vue';
import TextAreaField from '@/components/Form/TextAreaField.vue';
import { useEventSeriesInjection } from '@/event/composables/useEventSeriesInjection';
import { useForm } from '@/forms/composables/useForm';
import { computed } from 'vue';
import { EventSeriesAllocationNeeds } from '../schemas';

const props = defineProps<{ seriesId: string }>();
defineEmits<{ (e: 'handleFormField', v: { event: Event; fieldName: string }): void }>();

const seriesId = computed(() => props.seriesId);
const { series, updateEventSeries } = useEventSeriesInjection();

const { errors, values, setFieldValue, setFieldTouched, validate, onDiscard, resetForm } = useForm(
    EventSeriesAllocationNeeds,
    computed(() => ({
        notesHallExhibitionAreas: series.value?.notesHallExhibitionAreas ?? '',
        notesBoulevardSections: series.value?.notesBoulevardSections ?? '',
        notesPassages: series.value?.notesPassages ?? '',
        notesEntrances: series.value?.notesEntrances ?? '',
        notesServiceCenters: series.value?.notesServiceCenters ?? '',
        notesConferenceRooms: series.value?.notesConferenceRooms ?? '',
        notesOfficesInternal: series.value?.notesOfficesInternal ?? '',
        notesStorageInternal: series.value?.notesStorageInternal ?? '',
        notesOfficesExternal: series.value?.notesOfficesExternal ?? '',
        notesStorageExternal: series.value?.notesStorageExternal ?? '',
        notesAreas: series.value?.notesAreas ?? '',
        notesParking: series.value?.notesParking ?? '',
        otherAllocationNeeds: series.value?.otherAllocationNeeds ?? '',
    })),
);

async function save() {
    const valid = await validate();
    if (valid) {
        await updateEventSeries({ id: seriesId.value, ...values.value });
    }
}

defineExpose({ save, onDiscard, resetForm });
</script>
